import { useMutation, useQuery } from '@tanstack/react-query';
import useApi from 'api/mavapi/useApi';
import {
  MarkAllAsReadCommand,
  NotificationResponse,
  NotificationsService,
} from 'api/openapi';
import Cookies from 'js-cookie';
import { useOnErrorCallBack } from './useErrorHandling';
import { useDispatch } from 'react-redux';
import { useErrorHandlingSlice } from 'app/error/slice';
import usePushNotifications from './usePushNotifications';

export const useNotificationApi = () => {
  const { handleRequest } = useApi();
  const accessToken = Cookies.get('access_token');
  const dispatch = useDispatch();
  const { actions } = useErrorHandlingSlice();
  const { onErrorCallBack } = useOnErrorCallBack();
  const { currentNotificationToken } = usePushNotifications();
  const useGetNotifications = (pageNumber?: number) => {
    return useQuery<NotificationResponse>({
      queryKey: ['notificationList'],
      queryFn: () => {
        const response = handleRequest(
          NotificationsService.getApiNotifications(pageNumber, 10),
          false,
        );
        return response;
      },
      enabled: !!accessToken, // Only run the query if accessToken doesn't exist
      refetchOnWindowFocus: false, // Prevent refetching when switching tabs
    });
  };
  const useMarkAllRead = (
    requestBody?: MarkAllAsReadCommand,
    reFetchAfterRead?: () => void,
  ) => {
    return useMutation<string, Error, void>({
      mutationFn: () => {
        const response = handleRequest(
          NotificationsService.patchApiNotificationsMarkAsRead(requestBody),
        );
        return response;
      },
      onSuccess: data => {
        if (requestBody?.option === null) {
          Cookies.set('isRefetchNotification', 'true');
        }
        if (requestBody?.option?.length && data) {
          reFetchAfterRead?.();
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Marked all as read',
              show: true,
              errorType: 'success',
            }),
          );
        }
      },
      onError: error => {
        onErrorCallBack(error);
      },
    });
  };

  const useSendNotificationToken = () => {
    return useMutation<string, Error, void>({
      mutationFn: async () => {
        return await handleRequest(
          NotificationsService.postApiNotificationsPushnotificationToken({
            deviceId: 'web',
            token: currentNotificationToken,
          }),
          false,
        );
      },
    });
  };

  return {
    useGetNotifications,
    useMarkAllRead,
    useSendNotificationToken,
  };
};
